<template>
  <div class="minute-signature">
    <sign-plan
      class="minute-signature__sign-plan"
      :is-electronic-signature.sync="localModel.isElectronicSignature"
      :disabled="readonly || isSignPlanDisabled"
      question-label="¿Cuál será el mecanismo que se estará utilizando para firmar la Minuta de COMPU?"
      @change="emitChange"
    />

    <div v-if="isElectronic" class="mt-3">
      <signature-hub-info
        :signature-info="signatureHubInfo"
        @configure-sign="handleConfigureSign()"
      />
    </div>

    <template v-if="isManuscript">
      <div class="minute-signature__participants-section mt-3">
        <compu-minute-signatures
          v-model="localParticipants"
          class="minute-signature__participants"
          :options="availableSignatureOptions"
          :participation-types="participationTypeList"
          :group-name-overrides="participantGroupNames"
          :readonly="readonly"
        />
      </div>

      <div v-if="shouldShowDocumentSection" class="mt-3">
        <section-title has-line content="Documento" />

        <div class="minute-signature__document-card">
          <div class="minute-signature__document-info">
            <p class="minute-signature__label">Documento:</p>
            <p class="minute-signature__value">Minuta de COMPU Firmada</p>
          </div>

          <div class="minute-signature__document-actions">
            <action-button
              v-if="shouldShowDownloadTemplateButton"
              class="minute-signature__download-template"
              i="fas fa-file-download mr-n1 pr-2"
              text="Descargar Documento"
              variant="outline-success"
              @click="handleDownloadTemplate"
            />

            <action-button
              v-if="readonly"
              class="minute-signature__download-template"
              i="fas fa-file-download mr-n1 pr-2"
              text="Descargar Documento"
              variant="outline-success"
              :disabled="!manuscriptDocumentId"
              @click="downloadManuscriptDocument"
            />

            <custom-file-input
              v-else-if="shouldShowSignedFileInput"
              id="compuMinuteSignedDocument"
              ref="signedFileInputRef"
              v-model="localModel.signedDocumentFile"
              accept=".pdf"
              class="w-100 minute-signature__file-input"
              display-style="normal"
              label=""
              :max-size="5"
              can-delete
              :on-download="noopDownload"
              @delete="removeSignedDocument"
              @input="onSignedFileSelected"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';
import SectionTitle from 'SectionTitle';
import { SignPlan } from '@/components/sign-plan';
import { SignatureHubInfo } from '@quisit/signature-hub';
import { getSignatureHubInfo } from '@/services/signatureHub/api';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';
import signatureService from '@/modules/common/signature.api';
import CompuMinuteSignatures from './CompuMinuteSignatures.vue';
import compuMinuteStatus from '@/utils/constants/compuMinuteStatus.js';

export default {
  name: 'MinuteSignatureTab',
  components: {
    ActionButton,
    CompuMinuteSignatures,
    CustomFileInput,
    SectionTitle,
    SignPlan,
    SignatureHubInfo,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    minute: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    rebuildTemplate: {
      type: Boolean,
      default: false,
    },
    studentSieId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      localModel: this.buildLocalModel(this.value),
      localParticipants: this.buildParticipants(this.value?.participants),
      availableSignatureOptions: [],
      participationTypeList: [],
      signatureCounter: 0,
      hasDownloadedTemplate: false,
      isSyncingParticipants: false,
      isLoadingDefaultConfiguration: false,
      defaultConfigurationLoadedForStudentSieId: null,
      signatureHubInfo: null,
      participantGroupNames: {
        0: 'Funcionario',
        1: 'Representante de Familia',
        4: 'Representante Programa de Educación Especial',
        5: 'Representante DEPR',
      },
    };
  },
  computed: {
    minuteId() {
      return this.minute?.id ?? this.minute?.compuMinuteId ?? null;
    },
    isSignPlanDisabled() {
      return Number(this.minute?.statusId) === compuMinuteStatus.Completado;
    },
    isSignatureHubDisabled() {
      const statusId = Number(this.minute?.statusId);
      return (
        statusId === compuMinuteStatus.DocumentoCancelado ||
        statusId === compuMinuteStatus.FirmaRechazada
      );
    },
    isManuscript() {
      return this.localModel.isElectronicSignature === false;
    },
    isElectronic() {
      return this.localModel.isElectronicSignature === true;
    },
    hasCompleteParticipants() {
      return this.getParticipantsPayload().every(
        (participant) =>
          Boolean(participant.fullName?.trim()) &&
          Boolean(participant.positionId) &&
          Boolean(participant.participationTypeId)
      );
    },
    shouldShowDocumentSection() {
      return this.readonly || this.hasCompleteParticipants;
    },
    shouldShowDownloadTemplateButton() {
      return (
        !this.readonly &&
        this.shouldShowDocumentSection &&
        !this.hasDownloadedTemplate &&
        !this.localModel.signedDocumentFile
      );
    },
    shouldShowSignedFileInput() {
      return (
        !this.readonly &&
        this.shouldShowDocumentSection &&
        (this.hasDownloadedTemplate ||
          Boolean(this.localModel.signedDocumentFile))
      );
    },
    manuscriptDocument() {
      return (
        this.minute?.manuscriptDocument ||
        this.minute?.manuscriptSignedDocument ||
        this.value?.manuscriptDocument ||
        this.value?.manuscriptSignedDocument ||
        null
      );
    },
    manuscriptDocumentId() {
      return (
        this.manuscriptDocument?.documentId ||
        this.manuscriptDocument?.id ||
        this.minute?.manuscriptDocumentId ||
        this.minute?.manuscriptSignedDocumentId ||
        this.minute?.digitalSignatureDocumentId ||
        this.value?.manuscriptDocumentId ||
        this.value?.manuscriptSignedDocumentId ||
        this.value?.digitalSignatureDocumentId ||
        null
      );
    },
    manuscriptDocumentName() {
      return (
        this.manuscriptDocument?.documentName ||
        this.manuscriptDocument?.documentFilename ||
        this.manuscriptDocument?.filename ||
        this.manuscriptDocument?.fileName ||
        this.manuscriptDocument?.name ||
        'Minuta de COMPU Firmada.pdf'
      );
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        const nextModel = this.buildLocalModel(value);
        const nextParticipants = this.buildParticipants(value?.participants);

        if (
          nextModel.isElectronicSignature !==
            this.localModel.isElectronicSignature ||
          nextModel.signedDocumentFile !== this.localModel.signedDocumentFile
        ) {
          this.localModel = nextModel;
        }

        if (
          JSON.stringify(nextParticipants) !==
          JSON.stringify(this.localParticipants)
        ) {
          this.isSyncingParticipants = true;
          this.localParticipants = nextParticipants;
          this.$nextTick(() => {
            this.isSyncingParticipants = false;
          });
        }
      },
    },
    'localModel.isElectronicSignature'() {
      if (this.localModel.isElectronicSignature !== false) {
        this.localModel.signedDocumentFile = null;
        this.hasDownloadedTemplate = false;
      }

      if (this.localModel.isElectronicSignature === true) {
        this.fetchSignatureHubInfo();
      } else {
        this.signatureHubInfo = null;
        this.ensureDefaultParticipants();
      }

      this.emitChange();
    },
    studentSieId(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.defaultConfigurationLoadedForStudentSieId = null;

      if (this.localModel.isElectronicSignature === false) {
        this.ensureDefaultParticipants();
      }
    },
    rebuildTemplate(newValue) {
      if (newValue === true) {
        this.hasDownloadedTemplate = false;
        this.localModel.signedDocumentFile = null;
      }
    },
    minuteId(newId, oldId) {
      if (
        newId &&
        newId !== oldId &&
        this.localModel.isElectronicSignature === true
      ) {
        this.fetchSignatureHubInfo();
      }
    },
    localParticipants: {
      deep: true,
      handler() {
        if (this.isSyncingParticipants) {
          return;
        }

        if (this.localModel.signedDocumentFile) {
          this.localModel.signedDocumentFile = null;
        }

        this.hasDownloadedTemplate = false;
        this.emitChange();
      },
    },
  },
  async mounted() {
    try {
      await this.fetchParticipantOptions();
      if (this.localModel.isElectronicSignature === true) {
        await this.fetchSignatureHubInfo();
      } else if (this.localModel.isElectronicSignature === false) {
        await this.ensureDefaultParticipants();
      }
    } finally {
      this.$emit('ready');
    }
  },
  methods: {
    async fetchSignatureHubInfo() {
      if (!this.minuteId) return;
      try {
        const { data } = await getSignatureHubInfo(
          SignatureHubTypes.COMPU_MINUTE,
          this.minuteId
        );
        this.signatureHubInfo = data ?? null;
      } catch {
        this.signatureHubInfo = null;
      }
      this.$emit('hub-info-updated', this.signatureHubInfo);
    },
    handleConfigureSign() {
      this.$emit('configure-sign');
    },
    buildLocalModel(value) {
      return {
        isElectronicSignature:
          value?.isElectronicSignature ??
          this.minute?.isElectronicSignature ??
          null,
        signedDocumentFile: value?.signedDocumentFile ?? null,
      };
    },
    async fetchParticipantOptions() {
      const [positionsResponse, participationTypeResponse] = await Promise.all([
        signatureService.getAvailableSignatureOptions(),
        signatureService.getParticipationTypeList(),
      ]);

      this.availableSignatureOptions = positionsResponse.data ?? [];
      this.participationTypeList = participationTypeResponse.data ?? [];
      this.isSyncingParticipants = true;
      this.localParticipants = this.buildParticipants(this.value?.participants);
      this.$nextTick(() => {
        this.isSyncingParticipants = false;
      });
    },
    async ensureDefaultParticipants() {
      if (
        this.readonly ||
        this.localModel.isElectronicSignature !== false ||
        !this.studentSieId ||
        this.isLoadingDefaultConfiguration
      ) {
        return;
      }

      const studentKey = String(this.studentSieId);

      if (this.defaultConfigurationLoadedForStudentSieId === studentKey) {
        return;
      }

      if (
        this.localParticipants.some((participant) =>
          this.participantHasContent(participant)
        )
      ) {
        this.defaultConfigurationLoadedForStudentSieId = studentKey;
        return;
      }

      this.isLoadingDefaultConfiguration = true;

      try {
        const { data } = await signatureService.getDefaultConfiguration(
          null,
          this.studentSieId
        );
        const defaultParticipants = this.extractDefaultParticipants(data).map(
          (participant) => ({
            ...participant,
            isRequired: true,
          })
        );
        this.defaultConfigurationLoadedForStudentSieId = studentKey;

        if (!defaultParticipants.length) {
          return;
        }

        this.isSyncingParticipants = true;
        this.localParticipants = this.buildParticipants(defaultParticipants);
        this.$nextTick(() => {
          this.isSyncingParticipants = false;
          this.emitChange();
        });
      } catch {
        this.defaultConfigurationLoadedForStudentSieId = studentKey;
      } finally {
        this.isLoadingDefaultConfiguration = false;
      }
    },
    extractDefaultParticipants(data) {
      const payload = data?.data ?? data;

      if (Array.isArray(payload)) {
        return payload;
      }

      const candidateLists = [
        payload?.signatures,
        payload?.participants,
        payload?.signatureConfigurations,
        payload?.defaultConfigurations,
        payload?.defaultSignatures,
        payload?.items,
        payload?.configurations,
      ];

      return (
        candidateLists.find((candidate) => Array.isArray(candidate)) ?? []
      );
    },
    participantHasContent(participant) {
      return Boolean(
        participant?.signature?.name?.trim() ||
          participant?.signature?.positionId ||
          participant?.signature?.participationId ||
          participant?.signature?.alternateId
      );
    },
    buildParticipants(participants) {
      const normalizedParticipants = Array.isArray(participants)
        ? participants.map((participant) =>
            this.normalizeParticipant(participant)
          )
        : [];
      const predefinedGroups = [1, 5, 4];
      const predefinedParticipants = predefinedGroups.map((groupId) => {
        const existing = normalizedParticipants.find(
          (participant) => participant.groupId === groupId
        );

        return existing
          ? {
              ...existing,
            }
          : this.createParticipant(groupId, false);
      });
      const additionalParticipants = normalizedParticipants.filter(
        (participant) => !predefinedGroups.includes(participant.groupId)
      );

      return [...predefinedParticipants, ...additionalParticipants];
    },
    createParticipant(groupId = 0, isRemovable = true) {
      this.signatureCounter -= 1;

      return {
        id: this.signatureCounter,
        signature: {
          name: '',
          positionId: null,
          participationId: null,
          alternateId: null,
        },
        groupId,
        isRequired: false,
        isRemovable,
        isDisabled: false,
      };
    },
    normalizeParticipant(participant) {
      const hasDefaultConfigurationFlag =
        participant.value !== undefined || participant.Value !== undefined;
      const isDefaultConfiguration =
        hasDefaultConfigurationFlag &&
        participant.groupId === undefined &&
        participant.attendanceGroupId === undefined &&
        participant.signatureGroupId === undefined;
      const groupId =
        participant.groupId ??
        participant.attendanceGroupId ??
        participant.signatureGroupId ??
        participant.position?.signatureGroupId ??
        participant.signaturePosition?.signatureGroupId ??
        participant.signature?.positionId?.signatureGroupId ??
        (isDefaultConfiguration ? participant.id : undefined) ??
        0;

      const isRequired =
        participant.isRequired ??
        participant.required ??
        isDefaultConfiguration;
      const isRemovable =
        this.getParticipantRemovableFlag(participant) ??
        this.normalizeBoolean(participant.isRemovable) ??
        this.normalizeBoolean(participant.removable) ??
        (isDefaultConfiguration ? false : groupId === 0);

      return {
        id:
          (isDefaultConfiguration ? undefined : participant.id) ??
          participant.participantId ??
          this.signatureCounter--,
        signature: {
          name: this.getParticipantName(participant, isDefaultConfiguration),
          positionId: this.normalizePosition(participant),
          participationId: this.normalizeParticipation(participant),
          alternateId: this.normalizeAlternateParticipation(participant),
        },
        groupId,
        isRequired,
        isRemovable,
        isDisabled: false,
      };
    },
  getParticipantRemovableFlag(participant) {
    return this.normalizeBoolean(
      participant.isRemovableFlag ??
        participant.IsRemovableFlag ??
        participant.removableFlag ??
        participant.RemovableFlag ??
        participant.isAdditional ??
        participant.IsAdditional ??
        participant.value ??
        participant.Value
    );
  },
    normalizeBoolean(value) {
      if (value === undefined || value === null) {
        return undefined;
      }

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'number') {
        return value === 1;
      }

      if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();

        if (normalizedValue === 'true' || normalizedValue === '1') {
          return true;
        }

        if (normalizedValue === 'false' || normalizedValue === '0') {
          return false;
        }
      }

      return undefined;
    },
    getParticipantName(participant, isDefaultConfiguration = false) {
      const explicitName =
        participant.signature?.name ??
        (isDefaultConfiguration
          ? undefined
          : participant.fullName ??
            participant.name ??
            participant.userFullName ??
            participant.displayName);

      if (explicitName) {
        return explicitName;
      }

      return [
        participant.firstName,
        participant.initial,
        participant.firstLastName,
        participant.secondLastName,
      ]
        .filter(Boolean)
        .join(' ');
    },
    normalizePosition(participant) {
      const positionId =
        participant.signature?.positionId?.signaturePositionId ??
        participant.signaturePositionId ??
        participant.positionId ??
        participant.signaturePosition?.signaturePositionId ??
        participant.signaturePosition?.id ??
        participant.position?.signaturePositionId ??
        participant.position?.id ??
        participant.positionCode;

      if (!positionId) {
        return null;
      }

      return (
        this.availableSignatureOptions.find(
          (option) => option.signaturePositionId == positionId
        ) ?? { signaturePositionId: positionId }
      );
    },
    normalizeParticipation(participant) {
      const participationId =
        participant.signature?.participationId?.id ??
        participant.signatureParticipationTypeId ??
        participant.participationId ??
        participant.participationType?.id ??
        participant.participation?.id ??
        participant.participationType ??
        participant.participationTypeId;

      if (!participationId) {
        return null;
      }

      return (
        this.participationTypeList.find(
          (participationType) => participationType.id == participationId
        ) ?? { id: Number(participationId) }
      );
    },
    normalizeAlternateParticipation(participant) {
      const alternateId =
        participant.signature?.alternateId?.id ??
        participant.alternateParticipationType ??
        participant.alternateParticipationTypeId;

      return alternateId ? { id: Number(alternateId) } : undefined;
    },
    getParticipantsPayload() {
      return this.localParticipants.map((participant) => ({
        id: participant.id,
        groupId: participant.groupId,
        fullName: participant.signature?.name ?? '',
        positionId:
          participant.signature?.positionId?.signaturePositionId ?? null,
        participationTypeId: participant.signature?.participationId?.id ?? null,
        alternateParticipationTypeId:
          participant.signature?.alternateId?.id ?? null,
        isAdditional: participant.isRemovable,
      }));
    },
    onSignedFileSelected(file) {
      this.localModel.signedDocumentFile = file || null;
      this.emitChange();
    },
    removeSignedDocument() {
      this.localModel.signedDocumentFile = null;
      this.emitChange();
    },
    noopDownload() {},
    handleDownloadTemplate() {
      this.$emit('download-template', () => {
        this.hasDownloadedTemplate = true;
      });
    },
    emitChange() {
      const payload = {
        ...this.localModel,
        participants: this.getParticipantsPayload(),
        hasCompleteParticipants: this.hasCompleteParticipants,
      };

      this.$emit('input', payload);
      this.$emit('change', payload);
    },
    downloadManuscriptDocument() {
      if (!this.manuscriptDocumentId) {
        return;
      }

      this.$emit('download-signed-document', this.manuscriptDocumentId);
    },
  },
};
</script>

<style scoped lang="scss">
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.minute-signature {
  &__document-card {
    display: grid;
    grid-template-columns: 1fr minmax(280px, 360px);
    gap: 1rem;
    align-items: center;
    margin-top: 1rem;
    padding: 1rem;
    background: #f4f6f3;
  }

  &__label {
    margin-bottom: 0.25rem;
    color: map.get($colors, 'gray-6');
    font-size: 14px;
  }

  &__value {
    margin-bottom: 0;
    color: #11291b;
    font-weight: 500;
  }

  &__document-actions {
    width: 100%;
  }

  &__download-template {
    width: 100%;
  }

  &__file-input ::v-deep .custom-file-input-wrapper,
  &__file-input ::v-deep .file-input-container {
    margin: 0;
  }
}

.signature-hub-info--disabled {
  pointer-events: none;
  opacity: 0.5;
}

@media (max-width: 768px) {
  .minute-signature__document-card {
    grid-template-columns: 1fr;
  }
}
</style>
